import type { FormResponse } from "@/models/new/formResponse.types"
import type { Form, Field } from "@/models/new/forms.types"
import { shouldShowElement } from "@/utils/conditionalDisplay"

import type { ReportCreationMedia } from "./report-creation-media"

const isMissing = (value: FormResponse["responses"][string] | undefined): boolean => {
  if (!value) return true
  return (
    value.value === "" ||
    value.value === null ||
    value.value === undefined ||
    (Array.isArray(value.value) && value.value.length === 0)
  )
}

const required = (field: Field): boolean =>
  field.type !== "dynamicGroup" && field.type !== "geolocation" && field.required

/** Shared web-create semantics: geolocation is optional and a pending file satisfies a file field. */
export const validateReportCreation = (
  form: Pick<Form, "fields">,
  formResponse: FormResponse,
  media: ReportCreationMedia,
  resolvedInstancesByGroup: Record<string, number[]> = {},
): string[] => {
  const missing: string[] = []

  for (const section of form.fields) {
    if (!shouldShowElement(section.conditionalDisplay, formResponse)) continue
    for (const field of section.fields) {
      if (!shouldShowElement(field.conditionalDisplay, formResponse)) continue
      if (field.type !== "dynamicGroup") {
        if (
          required(field) &&
          !(field.type === "file" && media.responseUploads[field.id]?.length) &&
          isMissing(formResponse.responses[field.id])
        ) {
          missing.push(field.label)
        }
        continue
      }

      const pattern = new RegExp(`^${field.id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\.(\\d+)\\.`)
      const instances = new Set<number>()
      for (const key of [
        ...Object.keys(formResponse.responses),
        ...Object.keys(media.responseUploads),
        ...Object.keys(media.fieldMedia),
      ]) {
        const match = pattern.exec(key)
        if (match) instances.add(Number(match[1]))
      }
      for (const instance of resolvedInstancesByGroup[field.id] ?? []) {
        instances.add(instance)
      }
      const ordered = [...instances].sort((a, b) => a - b)
      for (const instance of ordered) {
        for (let childIndex = 0; childIndex < field.fields.length; childIndex += 1) {
          const child: Field = field.fields[childIndex]
          if (
            !required(child) ||
            !shouldShowElement(child.conditionalDisplay, formResponse, {
              groupId: field.id,
              instance,
              fields: field.fields,
            })
          ) {
            continue
          }
          const key = `${field.id}.${instance}.${child.name}`
          if (
            !(child.type === "file" && media.responseUploads[key]?.length) &&
            isMissing(formResponse.responses[key])
          ) {
            missing.push(`${field.label} #${ordered.indexOf(instance) + 1} — ${child.label}`)
          }
        }
      }
    }
  }
  return missing
}
